import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage } = useLanguage();

  const navLinks = [
    { path: '/about', label: language === 'en' ? 'About Us' : 'Giới thiệu' },
    {
      path: '/products',
      label: language === 'en' ? 'Products' : 'Sản phẩm',
      hasDropdown: true,
      dropdownItems: [
        { path: '/products', label: language === 'en' ? 'Industrial Machinery' : 'Máy móc công nghiệp' },
        { path: '/products', label: language === 'en' ? 'MEP Equipment' : 'Thiết bị MEP' },
        { path: '/products', label: language === 'en' ? 'Water Pumps' : 'Máy bơm nước' },
        { path: '/products', label: language === 'en' ? 'Pipe Fittings & Valves' : 'Phụ kiện ống & van' },
        { path: '/products', label: language === 'en' ? 'Pool Systems & Spares' : 'Hệ thống hồ bơi' },
        { path: '/products', label: language === 'en' ? 'Water Treatment Chemicals' : 'Hóa chất xử lý nước' },
        { path: '/products', label: language === 'en' ? 'Engineering Spares' : 'Phụ tùng kỹ thuật' }
      ]
    },
    { path: '/services', label: language === 'en' ? 'Services' : 'Dịch vụ',
      hasDropdown: true,
      dropdownItems: [
        { path: '/services', label: language === 'en' ? 'CAD Production' : 'Sản xuất CAD' },
        { path: '/services', label: language === 'en' ? 'MEP Design Services' : 'Dịch vụ thiết kế MEP' }
      ]
    },
    { path: '/contact', label: language === 'en' ? 'Contact Us' : 'Liên hệ' },
    { path: '/faq', label: 'FAQ' },
  ];

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0 mr-8 md:mr-12">
            <img 
              src="/logo.png" 
              alt="IVARME Trading Company" 
              className="w-auto max-h-16 object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="48" viewBox="0 0 120 48"%3E%3Crect fill="%23264f78" width="120" height="48" rx="4"/%3E%3Ctext x="60" y="30" font-family="Arial, sans-serif" font-size="20" font-weight="bold" fill="white" text-anchor="middle"%3EIVARME%3C/text%3E%3C/svg%3E';
              }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              link.hasDropdown ? (
                <div
                  key={link.path}
                  className="relative group"
                >
                  <button
                    className={`flex items-center gap-1 text-gray-700 hover:text-[#264f78] transition-colors duration-200 font-medium ${
                      location.pathname.startsWith('/products') ? 'text-[#264f78]' : ''
                    }`}
                  >
                    {link.label}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-2">
                    {link.dropdownItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-[#264f78] transition-colors duration-200"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-gray-700 hover:text-[#264f78] transition-colors duration-200 font-medium ${
                    location.pathname === link.path ? 'text-[#264f78]' : ''
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 text-gray-700 hover:text-[#264f78] transition-colors duration-200"
              aria-label="Toggle language"
            >
              <Globe className="w-5 h-5" />
              <span className="text-sm font-medium">{language === 'en' ? 'EN' : 'VI'}</span>
            </button>

            {/* Request Quote Button */}
            <Link to="/request-quote" className="hidden md:block">
              <Button className="bg-[#264f78] hover:bg-[#1a3a57] text-white transition-colors duration-200">
                {language === 'en' ? 'Request Quote' : 'Yêu cầu báo giá'}
              </Button>
            </Link>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-700"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden py-4 space-y-2 border-t border-gray-200">
            {navLinks.map((link) => (
              <div key={link.path}>
                {link.hasDropdown ? (
                  <div>
                    <Link
                      to={link.path}
                      className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                    <div className="pl-4">
                      {link.dropdownItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    to={link.path}
                    className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                )}
              </div>
            ))}
            <Link
              to="/request-quote"
              className="block mx-4 mt-4"
              onClick={() => setMobileMenuOpen(false)}
            >
              <Button className="w-full bg-[#264f78] hover:bg-[#1a3a57] text-white">
                {language === 'en' ? 'Request Quote' : 'Yêu cầu báo giá'}
              </Button>
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
